import { store } from '../store.js'
import { formatPrice } from '../utils/helpers.js'
import { redirectToCheckout } from '../lib/stripe.js'
import '../styles/components/checkout.css'

const CUSTOM_SHIPPING_MIN_CENTS = 3000 // $30 minimum

// Module-level selected rate — reset on each renderCheckout call
let selectedRate = null // { serviceCode, label, price_cents }

export function renderCheckout(outlet) {
  selectedRate = null

  const cart = store.get('cart')

  if (cart.length === 0) {
    outlet.innerHTML = `
      <div class="section">
        <div class="container" style="text-align: center; max-width: 480px; margin: 0 auto;">
          <h1 class="section-title" style="margin-bottom: var(--space-4)">Your cart is empty</h1>
          <p class="text-secondary" style="margin-bottom: var(--space-8)">Add some products before checking out.</p>
          <a href="#/shop" class="btn btn-primary btn-lg">Shop Products</a>
        </div>
      </div>
    `
    return
  }

  const subtotal = store.cartTotal

  outlet.innerHTML = `
    <div class="section">
      <div class="container" style="max-width: 640px">
        <div class="section-eyebrow">Checkout</div>
        <h1 class="section-title" style="text-align:left; margin-bottom: var(--space-8)">Order Summary</h1>

        <!-- Cart items -->
        <div class="card" style="margin-bottom: var(--space-6)">
          <div style="padding: var(--space-5) var(--space-6)">
            ${cart.map(item => `
              <div style="display:flex; justify-content:space-between; align-items:center; padding-block: var(--space-3); border-bottom: 1px solid var(--color-border)">
                <div>
                  <div class="font-medium">${escapeHtml(item.name)}</div>
                  <div class="text-secondary text-sm">Qty: ${item.quantity}</div>
                </div>
                <div class="font-semibold">${formatPrice(item.price_cents * item.quantity)}</div>
              </div>
            `).join('')}
            <div style="display:flex; justify-content:space-between; padding-top: var(--space-4)" class="text-sm text-secondary">
              <span>Subtotal</span>
              <span>${formatPrice(subtotal)}</span>
            </div>
          </div>
        </div>

        <!-- Shipping calculator -->
        <div class="card" style="margin-bottom: var(--space-6)">
          <div style="padding: var(--space-5) var(--space-6)">
            <div class="checkout-shipping-header">
              <span class="font-medium">Shipping</span>
              <span class="text-sm text-secondary">Enter postal or zip code for rates</span>
            </div>
            <div class="checkout-postal-row">
              <input
                id="shipping-postal"
                class="form-input checkout-postal-input"
                type="text"
                placeholder="Postal / ZIP code"
                autocomplete="postal-code"
                maxlength="10"
              />
              <select id="shipping-country" class="form-input checkout-country-select">
                <option value="CA">Canada</option>
                <option value="US">United States</option>
              </select>
              <button id="get-rates-btn" class="btn btn-primary checkout-get-rates-btn">
                Get Rates
              </button>
            </div>
            <div id="rates-container"></div>
          </div>
        </div>

        <!-- Totals -->
        <div class="card" style="margin-bottom: var(--space-6)">
          <div style="padding: var(--space-4) var(--space-6)">
            <div style="display:flex; justify-content:space-between" class="text-sm text-secondary">
              <span>Subtotal</span>
              <span>${formatPrice(subtotal)}</span>
            </div>
            <div style="display:flex; justify-content:space-between; margin-top: var(--space-2)" class="text-sm text-secondary">
              <span>Shipping</span>
              <span id="shipping-display" style="font-style: italic">—</span>
            </div>
            <div class="divider" style="margin-block: var(--space-3)"></div>
            <div style="display:flex; justify-content:space-between; font-size: var(--text-xl); font-weight: var(--font-bold)">
              <span>Total</span>
              <span id="order-total">${formatPrice(subtotal)}</span>
            </div>
          </div>
        </div>

        <!-- Account creation -->
        <div class="card" style="margin-bottom: var(--space-6)">
          <div style="padding: var(--space-5) var(--space-6)">
            <label style="display:flex; align-items:center; gap: var(--space-3); cursor:pointer; margin-bottom: var(--space-3);">
              <input id="create-account-check" type="checkbox" style="width:16px; height:16px; accent-color: var(--color-accent);" />
              <span class="font-medium">Create an account</span>
              <span class="text-sm text-secondary">— track orders & get faster checkout</span>
            </label>
            <div id="account-fields" style="display:none;">
              <div class="checkout-postal-row" style="margin-top: var(--space-3);">
                <input id="account-email" class="form-input" type="email" placeholder="Email address" autocomplete="email" style="flex:2;" />
                <input id="account-password" class="form-input" type="password" placeholder="Password (min 6 chars)" autocomplete="new-password" style="flex:1;" />
              </div>
              <div id="account-error" style="display:none; color: var(--color-error); font-size: var(--text-xs); margin-top: var(--space-2);"></div>
            </div>
          </div>
        </div>

        <button class="btn btn-primary btn-lg" id="stripe-checkout-btn" style="width:100%">
          Continue to Payment →
        </button>

        <p style="text-align:center; margin-top: var(--space-4); font-size: var(--text-xs); color: var(--color-text-muted)">
          Secure payment powered by Stripe. We never store your card details.
        </p>

        <div style="text-align:center; margin-top: var(--space-4)">
          <button class="btn btn-ghost" onclick="history.back()">← Back to cart</button>
        </div>
      </div>
    </div>
  `

  const postalInput = document.getElementById('shipping-postal')
  const countrySelect = document.getElementById('shipping-country')

  // Auto-detect country from postal code format as user types
  postalInput.addEventListener('input', () => {
    const val = postalInput.value.trim()
    if (/^[A-Za-z]\d/.test(val)) {
      countrySelect.value = 'CA'
    } else if (/^\d/.test(val)) {
      countrySelect.value = 'US'
    }
  })

  document.getElementById('get-rates-btn').addEventListener('click', handleGetRates)
  postalInput.addEventListener('keydown', e => { if (e.key === 'Enter') handleGetRates() })
  document.getElementById('create-account-check').addEventListener('change', (e) => {
    document.getElementById('account-fields').style.display = e.target.checked ? 'block' : 'none'
    if (e.target.checked) document.getElementById('account-email').focus()
  })

  document.getElementById('stripe-checkout-btn').addEventListener('click', handleCheckout)

  // Render the rates list with only the Custom option visible until Shippo loads.
  const initialRatesContainer = document.getElementById('rates-container')
  renderRatesList([], initialRatesContainer, -1)
}

async function handleGetRates() {
  const postalInput = document.getElementById('shipping-postal')
  const countrySelect = document.getElementById('shipping-country')
  const ratesContainer = document.getElementById('rates-container')
  const getRatesBtn = document.getElementById('get-rates-btn')

  const postalCode = postalInput?.value.trim()
  if (!postalCode) {
    postalInput?.focus()
    return
  }

  const countryCode = countrySelect?.value || 'CA'

  // Loading state
  getRatesBtn.disabled = true
  getRatesBtn.innerHTML = '<span class="spinner" style="width:14px;height:14px;border-width:2px"></span>'
  ratesContainer.innerHTML = `
    <div class="checkout-rates-loading">
      <span class="spinner" style="width:16px;height:16px;border-width:2px"></span>
      Getting rates…
    </div>
  `

  const cart = store.get('cart')
  const items = cart.map(item => ({
    weight_lbs: item.weight_lbs || 1.0,
    quantity: item.quantity,
  }))

  try {
    const res = await fetch('/api/get-shipping-rates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ postalCode, countryCode, items }),
    })

    const data = await res.json()

    if (!res.ok || !Array.isArray(data.rates)) {
      throw new Error(data.error || 'Failed to get shipping rates')
    }

    if (data.rates.length === 0) {
      selectedRate = null
      updateTotal()
      renderRatesList([], ratesContainer, -1, 'No carrier rates returned for this destination. You can still use the Custom option below.')
      return
    }

    // Auto-select cheapest non-custom rate.
    selectedRate = data.rates[0]
    updateTotal()

    renderRatesList(data.rates, ratesContainer, 0)

  } catch (err) {
    console.error('[checkout] Get rates error:', err)
    ratesContainer.innerHTML = `
      <p class="text-sm" style="color: var(--color-error); margin-top: var(--space-3)">
        ${escapeHtml(err.message || 'Could not load shipping rates. Please try again.')}
      </p>
    `
  } finally {
    getRatesBtn.disabled = false
    getRatesBtn.textContent = 'Get Rates'
  }
}

function renderRatesList(shippoRates, container, selectedIdx = -1, warning = null) {
  if (!container) return

  // Custom shipping option always sits at the bottom.
  const customIdx = shippoRates.length

  container.innerHTML = `
    ${warning ? `<p class="checkout-rates-warning">${escapeHtml(warning)}</p>` : ''}
    <div class="checkout-rates-list" role="radiogroup" aria-label="Shipping options">
      ${shippoRates.map((rate, i) => `
        <label class="checkout-rate-option${i === selectedIdx ? ' checkout-rate-selected' : ''}">
          <input type="radio" name="shipping-rate" value="${i}"
            ${i === selectedIdx ? 'checked' : ''} class="checkout-rate-radio" />
          <span class="checkout-rate-label">${escapeHtml(rate.label)}</span>
          <span class="checkout-rate-price">${formatPrice(rate.price_cents)}</span>
        </label>
      `).join('')}
      <label class="checkout-rate-option checkout-rate-custom${selectedIdx === customIdx ? ' checkout-rate-selected' : ''}">
        <input type="radio" name="shipping-rate" value="custom"
          ${selectedIdx === customIdx ? 'checked' : ''} class="checkout-rate-radio" />
        <span class="checkout-rate-label">Custom (you have a quote from us)</span>
      </label>
      <div class="checkout-custom-amount" id="checkout-custom-amount"
           style="display:${selectedIdx === customIdx ? 'flex' : 'none'};">
        <span class="checkout-custom-prefix">$</span>
        <input id="checkout-custom-input" class="form-input checkout-custom-input"
               type="number" min="30" step="0.01" placeholder="0.00"
               inputmode="decimal" autocomplete="off" />
        <button type="button" class="btn btn-primary btn-sm" id="checkout-custom-apply">Apply</button>
        <span class="checkout-custom-hint">Minimum $30</span>
        <div class="checkout-custom-error" id="checkout-custom-error" style="display:none;"></div>
      </div>
    </div>
  `

  const list = container.querySelector('.checkout-rates-list')

  list.addEventListener('change', e => {
    // Only respond to the shipping-rate radios — the custom amount input
    // also lives inside this list and fires its own change events on blur.
    if (e.target.name !== 'shipping-rate') return

    const value = e.target.value
    const customPanel = document.getElementById('checkout-custom-amount')
    const customInput = document.getElementById('checkout-custom-input')
    const customErr = document.getElementById('checkout-custom-error')

    container.querySelectorAll('.checkout-rate-option').forEach(el => {
      el.classList.remove('checkout-rate-selected')
    })
    e.target.closest('.checkout-rate-option')?.classList.add('checkout-rate-selected')

    if (value === 'custom') {
      customPanel.style.display = 'flex'
      customInput.focus()
      selectedRate = null
      updateTotal()
    } else {
      customPanel.style.display = 'none'
      customInput.value = ''
      if (customErr) customErr.style.display = 'none'
      const idx = parseInt(value, 10)
      if (!isNaN(idx) && shippoRates[idx]) {
        selectedRate = shippoRates[idx]
        updateTotal()
      }
    }
  })

  const customInput = document.getElementById('checkout-custom-input')
  const applyBtn = document.getElementById('checkout-custom-apply')

  const applyCustom = () => {
    const errEl = document.getElementById('checkout-custom-error')
    const raw = customInput?.value.trim()
    const amount = parseFloat(raw)
    if (!raw || isNaN(amount) || amount <= 0) {
      if (errEl) { errEl.textContent = 'Enter a valid amount.'; errEl.style.display = 'block' }
      selectedRate = null
      updateTotal()
      return
    }
    const cents = Math.round(amount * 100)
    if (cents < CUSTOM_SHIPPING_MIN_CENTS) {
      if (errEl) {
        errEl.textContent = `Minimum is ${formatPrice(CUSTOM_SHIPPING_MIN_CENTS)}.`
        errEl.style.display = 'block'
      }
      selectedRate = null
      updateTotal()
      return
    }
    if (errEl) errEl.style.display = 'none'
    selectedRate = {
      serviceCode: 'custom',
      label: 'Custom shipping (quoted)',
      price_cents: cents,
    }
    updateTotal()
  }

  applyBtn?.addEventListener('click', applyCustom)
  customInput?.addEventListener('keydown', e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      applyCustom()
    }
  })
}

function updateTotal() {
  const subtotal = store.cartTotal
  const shippingDisplay = document.getElementById('shipping-display')
  const totalEl = document.getElementById('order-total')

  if (selectedRate) {
    if (shippingDisplay) {
      shippingDisplay.textContent = formatPrice(selectedRate.price_cents)
      shippingDisplay.style.fontStyle = ''
    }
    if (totalEl) totalEl.textContent = formatPrice(subtotal + selectedRate.price_cents)
  } else {
    if (shippingDisplay) {
      shippingDisplay.textContent = '—'
      shippingDisplay.style.fontStyle = 'italic'
    }
    if (totalEl) totalEl.textContent = formatPrice(subtotal)
  }
}

async function handleCheckout() {
  const btn = document.getElementById('stripe-checkout-btn')
  if (!btn) return

  if (!selectedRate) {
    let errEl = document.getElementById('checkout-error')
    if (!errEl) {
      errEl = document.createElement('p')
      errEl.id = 'checkout-error'
      errEl.style.cssText = 'color: var(--color-error); text-align:center; font-size: var(--text-sm); margin-top: var(--space-3)'
      btn.parentElement.insertBefore(errEl, btn.nextSibling)
    }
    errEl.textContent = 'Please get shipping rates and select a shipping option before continuing.'
    return
  }

  // Handle optional account creation
  const wantsAccount = document.getElementById('create-account-check')?.checked
  if (wantsAccount) {
    const email = document.getElementById('account-email')?.value.trim()
    const password = document.getElementById('account-password')?.value
    const accountErr = document.getElementById('account-error')
    if (!email || !password) {
      if (accountErr) { accountErr.textContent = 'Email and password required to create account.'; accountErr.style.display = 'block' }
      return
    }
    if (password.length < 6) {
      if (accountErr) { accountErr.textContent = 'Password must be at least 6 characters.'; accountErr.style.display = 'block' }
      return
    }
    if (accountErr) accountErr.style.display = 'none'

    try {
      const res = await fetch('/api/create-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      const data = await res.json()
      if (!res.ok) {
        if (accountErr) { accountErr.textContent = data.error || 'Failed to create account.'; accountErr.style.display = 'block' }
        return
      }
    } catch (err) {
      if (accountErr) { accountErr.textContent = 'Account creation failed. You can still continue.'; accountErr.style.display = 'block' }
    }
  }

  btn.disabled = true
  btn.innerHTML = '<span class="spinner" style="width:18px;height:18px;border-width:2px"></span> Redirecting to Stripe…'

  try {
    const cart = store.get('cart')
    await redirectToCheckout(cart, selectedRate)
    // If we reach here, redirect didn't happen — Stripe may be unconfigured
  } catch (err) {
    console.error('[checkout] Stripe redirect failed:', err)
    btn.disabled = false
    btn.textContent = 'Continue to Payment →'

    const container = btn.parentElement
    let errEl = document.getElementById('checkout-error')
    if (!errEl) {
      errEl = document.createElement('p')
      errEl.id = 'checkout-error'
      errEl.style.cssText = 'color: var(--color-error); text-align:center; font-size: var(--text-sm); margin-top: var(--space-3)'
      container.insertBefore(errEl, btn.nextSibling)
    }
    errEl.textContent = err.message || 'Payment unavailable. Please try again.'
  }
}

export function renderSuccess(outlet) {
  // Clear cart on successful payment
  store.clearCart()

  outlet.innerHTML = `
    <div class="section">
      <div class="container" style="max-width: 480px; text-align: center">
        <div style="width:80px;height:80px;background:rgba(16,185,129,0.1);border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto var(--space-6);color:var(--color-success)">
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
        </div>
        <h1 class="section-title" style="margin-bottom: var(--space-4)">Order confirmed!</h1>
        <p class="text-secondary" style="margin-bottom: var(--space-8)">
          Thank you for your order. You'll receive a confirmation email shortly with your tracking details.
        </p>
        <a href="#/shop" class="btn btn-primary btn-lg">Continue Shopping</a>
      </div>
    </div>
  `
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
